import {
  FinancialAccountTopUpConfigurationResponse,
  PaginationResultsEmployeeTableDataDto,
  PaginationResultsVehicleTableDataDto,
  OnboardingImportSummaryDataResponse,
  OnboardingSelectSubscriptionRequest,
  FinancialAccountNewCardTopUpRequest,
  OnboardingDataForEmployeesResponse,
  OnboardingSaveCompanyPolicyRequest,
  OnboardingCompanyPolicyResponse,
  AvailableSubscriptionsResponse,
  OnboardingEditVehicleRequest,
  OnboardingNextStepRequest,
  OnboardingAddEmployeeDto,
  OnboardingAddVehicleDto,
  EditEmployeeRequest,
  OnboardingInfoDto,
  OnboardingStep,
  SearchRequest,
} from '@interfaces/models';
import { httpClient } from '@api/interceptors';
import qs from 'qs';

// WRAPPER

export async function onboardingStatus(): Promise<OnboardingInfoDto> {
  return httpClient.get('onboarding/status').json();
}

// STEP 1

export async function saveVehicles(
  vehicles: OnboardingAddVehicleDto[],
  nextStep: OnboardingStep,
): Promise<void> {
  return httpClient.post('onboarding/vehicles', { json: { vehicles, nextStep } }).json();
}

// STEP 2

export async function getEmployeesData(): Promise<OnboardingDataForEmployeesResponse> {
  return httpClient.get('onboarding/employees/data').json();
}

export async function saveEmployees(
  employees: OnboardingAddEmployeeDto[],
  nextStep: OnboardingStep,
): Promise<void> {
  return httpClient.post('onboarding/employees', { json: { employees, nextStep } }).json();
}

// STEP 3

export async function getTopUpData(): Promise<FinancialAccountTopUpConfigurationResponse> {
  return httpClient.get('onboarding/top-up/data').json();
}

export async function saveTopUp(
  data: FinancialAccountNewCardTopUpRequest,
): Promise<{ redirectUri: string }> {
  return httpClient.post('onboarding/top-up', { json: { ...data } }).json();
}

// STEP 4

export async function getSubscriptionsData(): Promise<AvailableSubscriptionsResponse> {
  return httpClient.get('onboarding/subscriptions/data').json();
}

export async function saveSubscriptions(
  config: OnboardingSelectSubscriptionRequest,
): Promise<void> {
  return httpClient.post('onboarding/subscriptions', { json: config }).json();
}

// STEP 5

export async function getCompanyPolicyData(): Promise<OnboardingCompanyPolicyResponse> {
  return httpClient.get('onboarding/company-policy/data').json();
}

export async function saveCompanyPolicy(
  companyPolicy: OnboardingSaveCompanyPolicyRequest,
): Promise<void> {
  return httpClient.post('onboarding/company-policy', { json: companyPolicy }).json();
}

// IMPORT STEP 1

export async function importCsvFile(file: File, nextStep: OnboardingStep): Promise<void> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('nextStep', nextStep);

  return httpClient.post('onboarding/import-bulk', { body: formData, timeout: 180000 }).json();
}

// IMPORT STEP 2

export async function getImportSummaryData(): Promise<OnboardingImportSummaryDataResponse> {
  return httpClient.get('onboarding/import-summary/data').json();
}

export async function getImportSummaryErrors(): Promise<Blob> {
  return httpClient.get('onboarding/import-summary/errors', { timeout: 180000 }).blob();
}

export async function importSummary(nextStep: OnboardingStep): Promise<void> {
  return httpClient
    .post('onboarding/import-summary', { json: { nextStep }, timeout: 180000 })
    .json();
}

export async function resetOnboarding(): Promise<void> {
  return httpClient.get('onboarding/reset', { timeout: 180000 }).json();
}

export async function setOnboardingStep(nextStep: OnboardingStep): Promise<void> {
  return httpClient.put('onboarding/next-step', { json: { nextStep } }).json();
}

export async function getOnboardingImportVehicles(
  request: SearchRequest,
): Promise<PaginationResultsVehicleTableDataDto> {
  return httpClient
    .get('onboarding/import-vehicles', {
      searchParams: qs.stringify(request, { allowDots: true }),
    })
    .json();
}

export async function removeOnboardingImportedVehicle(vehicleId: string): Promise<void> {
  return httpClient.delete(`onboarding/import-vehicles/${vehicleId}`).json();
}

export async function editOnboardingImportedVehicle(
  vehicle: OnboardingEditVehicleRequest,
  vehicleId: string,
): Promise<void> {
  return httpClient.put(`onboarding/import-vehicles/${vehicleId}`, { json: vehicle }).json();
}

export async function saveImportedVehicles(payload: OnboardingNextStepRequest): Promise<void> {
  return httpClient.put('onboarding/next-step', { json: payload }).json();
}

export async function getOnboardingImportEmployees(
  request: SearchRequest,
): Promise<PaginationResultsEmployeeTableDataDto> {
  return httpClient
    .get('onboarding/import-employees', {
      searchParams: qs.stringify(request, { allowDots: true }),
    })
    .json();
}

export async function removeOnboardingImportedEmployee(employeeId: string): Promise<void> {
  return httpClient.delete(`onboarding/import-employees/${employeeId}`).json();
}

export async function editOnboardingImportedEmployee(
  employee: EditEmployeeRequest,
  employeeId: string,
): Promise<void> {
  return httpClient
    .put(`onboarding/import-employees/${employeeId}`, { json: { ...employee, id: employeeId } })
    .json();
}

export async function saveImportedEmployees(payload: OnboardingNextStepRequest): Promise<void> {
  return httpClient.put('onboarding/next-step', { json: payload }).json();
}

export async function skipOnboarding(): Promise<void> {
  return httpClient.post(`onboarding/skip`).json();
}
